import React, { useCallback, useState } from "react";

import {
  Box,
  Flex,
  FlexProps,
  IconDownArrow,
  IconHamburger,
  Popover,
  PopoverControl,
  Span,
} from "@catchoftheday/cg-components";
import { minusOnePx, styled, themeGet } from "@catchoftheday/theme";

import { useIsTabletView } from "@header/hooks/useIsTabletView";
import { Shop } from "@header/types";

import { trackMenuImpression } from "../../utilities/analytics";
import { CategoryMenu } from "../CategoryMenu";
import { CATEGORY_MENU_DESKTOP_WIDTH, POPOVER_ZINDEX } from "../consts";
import { MenuItem } from "../MenuItem";
import { SlideInMenu } from "../SlideInMenu";

import { getNavData } from "./utilities";

const BOX_SHADOW_STYLE = "box-shadow: 0px -5px 10px #bfbfbf;";

const MenuContainer = styled(Box)`
  @media (max-width: ${(props) =>
      minusOnePx(
        themeGet("breakpoints.lg")(props)
      )}) and (min-width: ${themeGet("breakpoints.md")}) {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
  }
`;

type ShopAllCategoriesProps = FlexProps & {
  isSubmenuOpen?: boolean;
};

const ShopAllCategoriesButton = styled(Flex)<ShopAllCategoriesProps>`
  height: 100%;
  font-size: ${themeGet("fontSizes.sm")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0px 10px;
  margin-right: 57px;
  ${(props) => props.isSubmenuOpen && BOX_SHADOW_STYLE}

  @media (min-width: ${(props) => themeGet("breakpoints.lg")(props)}) {
    &:hover {
      background-color: white;
      ${BOX_SHADOW_STYLE}
    }
    ${(props) => props.isSubmenuOpen && "background-color: white;"}
  }

  @media (max-width: ${(props) =>
      minusOnePx(themeGet("breakpoints.lg")(props))}) and (min-width: ${(
      props
    ) => themeGet("breakpoints.md")(props)}) {
    padding: 6px 16px;
    border-radius: 100px;
    border: 1px solid;
    border-color: ${(props) => themeGet("colors.grey")(props)};
    margin-left: 24px;
    margin-right: 4px;
    height: auto;

    &:active {
      box-shadow: inset 0px 4px 4px ${(props) => themeGet("colors.grey")(props)};
    }
  }
`;

const ShopAllCategoriesChildren = () => (
  <>
    <IconHamburger
      display={{ md: "block", lg: "none" }}
      width="18px"
      height="auto"
      mr="10px"
    />
    <Span mr="12px" lineHeight="1" fontWeight={{ md: "normal", lg: "light" }}>
      Shop All Categories
    </Span>
    <IconDownArrow
      display={{ md: "none", lg: "block" }}
      width="8px"
      height="auto"
    />
  </>
);

const CategoryMenuContainer = styled(Flex)`
  position: absolute;
  top: -2px;
  width: ${CATEGORY_MENU_DESKTOP_WIDTH}px;
  cursor: pointer;
  background-color: ${(props) =>
    themeGet("colors.ui.backgroundColorSecondary")(props)};
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.25);
  z-index: 101;
  transition: max-height 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) 0s;

  &:before {
    content: "";
    position: absolute;
    width: 168px;
    height: 16px;
    top: -16px;
    background-color: inherit;
  }
`;

type MenuBarProps = {
  shops: Shop[];
  checkScrollPosition: (scroller: HTMLDivElement) => void;
  recordScrollPosition: () => void;
};

export const MenuBar = React.forwardRef<HTMLDivElement, MenuBarProps>(
  ({ shops, checkScrollPosition, recordScrollPosition }, ref) => {
    const isTabletView = useIsTabletView();
    const [touchedItem, setTouchedItem] = useState<number>();
    const { primaryNav, seasonalNav } = getNavData(shops);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [focusItemIndex, setFocusItemIndex] = useState(0);

    const [categoryMenuContainerMaxHeight, setCategoryMenuContainerMaxHeight] =
      useState(0);

    const showMenu = () => setIsMenuOpen(true);
    const hideMenu = () => setIsMenuOpen(false);

    const showAllButtons = ({
      ref,
      isOpen,
      handleOnKeyDown,
      handleOnBlur,
      handleOnMouseEnter,
      handleOnMouseLeave,
    }) => {
      const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = (
        e
      ) => {
        if (
          e.code === "ArrowDown" ||
          e.code === "Enter" ||
          e.code === "Space" ||
          e.code === "Escape"
        ) {
          e.preventDefault();
          if (e.code !== "Escape") {
            trackShopAllCategoriesButtonImpression();
          }
          setTimeout(
            () => document.getElementById(`category-menu-item-0`)?.focus(),
            100
          );
          handleOnKeyDown(e);
        }
      };
      return (
        <ShopAllCategoriesButton
          id="menu-bar-item-0"
          role="button"
          aria-label="Open category menu"
          tabIndex={focusItemIndex == 0 ? 0 : -1}
          aria-expanded={true}
          ref={ref}
          isSubmenuOpen={isOpen}
          onKeyDown={keyDownHandler}
          onBlur={handleOnBlur}
          onMouseEnter={() => {
            handleOnMouseEnter();
            trackShopAllCategoriesButtonImpression();
          }}
          onMouseLeave={handleOnMouseLeave}
        >
          <ShopAllCategoriesChildren />
        </ShopAllCategoriesButton>
      );
    };

    const trackShopAllCategoriesButtonImpression = useCallback(() => {
      trackMenuImpression({
        name: "Shop All Categories",
        item_path: [
          {
            name: "Shop All Categories",
            id: 0,
            position: 0,
          },
        ],
      });
    }, []);

    const setShowAllFocus = useCallback(() => {
      document.getElementById("menu-bar-item-0")?.focus();
    }, []);

    const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> =
      useCallback(
        (e) => {
          switch (e.code) {
            case "ArrowLeft":
              e.preventDefault();
              if (focusItemIndex !== 0) {
                document
                  .getElementById(`menu-bar-item-${focusItemIndex - 1}`)
                  ?.focus();
                setFocusItemIndex(focusItemIndex - 1);
              }
              break;
            case "ArrowRight":
              e.preventDefault();
              if (focusItemIndex !== seasonalNav.length) {
                document
                  .getElementById(`menu-bar-item-${focusItemIndex + 1}`)
                  ?.focus();
                setFocusItemIndex(focusItemIndex + 1);
              }
              break;
            case "Home":
              e.preventDefault();
              document.getElementById(`menu-bar-item-0`)?.focus();
              setFocusItemIndex(0);
              break;
            case "End":
              e.preventDefault();
              document
                .getElementById(`menu-bar-item-${seasonalNav.length}`)
                ?.focus();
              setFocusItemIndex(seasonalNav.length);
              break;
            case "Space":
              e.preventDefault();
              document
                .getElementById(`menu-bar-item-${focusItemIndex}`)
                ?.click();
              break;
            default:
              break;
          }
        },
        [seasonalNav.length, focusItemIndex]
      );

    return (
      <>
        <MenuContainer
          role="menubar"
          aria-label="Desktop navigation"
          as="nav"
          display="flex"
          height="50px"
          color="text"
          position="relative"
          overflow="visible"
          flexWrap="wrap"
          justifyContent="flex-start"
          alignItems="center"
          onKeyDown={keyDownHandler}
          onScroll={(e) => checkScrollPosition(e.currentTarget)}
          ref={ref}
        >
          {isTabletView ? (
            <ShopAllCategoriesButton
              role="button"
              aria-label="Open category menu"
              tabIndex={0}
              aria-expanded={isMenuOpen}
              onClick={() => {
                showMenu();
                trackShopAllCategoriesButtonImpression();
              }}
            >
              <ShopAllCategoriesChildren />
            </ShopAllCategoriesButton>
          ) : (
            <Popover
              height={{ xs: "auto", lg: "100%" }}
              placement="bottom"
              fullWidth
              hideOnLeave
              hideOnLeaveInterval={100}
              hideArrow
              zIndex={POPOVER_ZINDEX}
              enhancedKeyboardNavigation
              modifier={[
                {
                  name: "flip",
                  enabled: true,
                },
              ]}
            >
              <PopoverControl>{showAllButtons}</PopoverControl>
              <CategoryMenuContainer
                aria-label="Category Menu"
                maxHeight={categoryMenuContainerMaxHeight}
              >
                <CategoryMenu
                  shops={primaryNav}
                  setCategoryMenuContainerMaxHeight={
                    setCategoryMenuContainerMaxHeight
                  }
                  setParentFocus={setShowAllFocus}
                />
              </CategoryMenuContainer>
            </Popover>
          )}
          <Flex
            height="100%"
            flex="1"
            overflow={{ md: "visible", lg: "hidden" }}
            flexWrap={{ md: "nowrap", lg: "wrap" }}
          >
            {/* NOTE: index 0 is reserved for ShopAllCategoriesButton, so we prepend a null value to the array. */}
            {[null, ...seasonalNav].map(
              (shop, index) =>
                shop && (
                  <MenuItem
                    index={index}
                    tabIndex={focusItemIndex == index ? 0 : -1}
                    key={shop.id}
                    shop={shop}
                    touchedItem={touchedItem}
                    setTouchedItem={setTouchedItem}
                    itemPathShop={[
                      { name: shop.name, id: shop.id, position: index },
                    ]}
                    recordScrollPosition={recordScrollPosition}
                  />
                )
            )}
          </Flex>
        </MenuContainer>
        {isMenuOpen && isTabletView && (
          <SlideInMenu shops={primaryNav} hideMenu={hideMenu} />
        )}
      </>
    );
  }
);

MenuBar.displayName = "MenuBar";
export default MenuBar;
